import React, { useContext, useState, useEffect } from 'react';
import { AppContext } from '../context/AppContext';
import profileImage from '../images/Rami-Profile-Pic.jpg';
import { motion, AnimatePresence } from 'framer-motion';
import { FaFileDownload, FaBriefcase, FaPhone } from 'react-icons/fa';
import './CubeStyles.css';

const Overview: React.FC = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('Overview must be used within an AppProvider');
    }
    const { dispatch } = context;

    const [currentStatement, setCurrentStatement] = useState(0);
    const statements = [
        "Building Tomorrow's Tech, Today",
        "Turning Bold Ideas into Digital Reality",
        "Driving Disruption Through Code",
        "Empowering Teams to Deliver Excellence",
    ];

    const [animate, setAnimate] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentStatement((prev) => (prev + 1) % statements.length);
        }, 3000);

        const timer = setTimeout(() => setAnimate(true), 3000);

        return () => {
            clearInterval(interval);
            clearTimeout(timer);
        };
    }, [statements.length]);

    const handleNavigate = (path: string) => {
        dispatch({ type: 'SET_SECTION', payload: path });
    };

    return (
        <section id="overview" className="relative min-h-screen bg-black text-[#39ff14] p-8 pb-24 overflow-hidden flex items-center justify-center">

            <div className="flex flex-col self-center md:flex-row items-center md:items-start text-center md:text-left space-y-8 md:space-y-0 md:space-x-12 w-[60%]">
                <motion.div
                    className={`w-80 h-80 md:w-96 md:h-96 rounded-full overflow-hidden shadow-lg border-4 border-[#39ff14] flex-shrink-0 ${animate ? 'animate-pulse-border' : ''}`}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8 }}
                >
                    <img
                        src={profileImage}
                        alt="Rami Ghaleb"
                        className="object-cover w-full h-full object-[0%_10%]"
                    />
                </motion.div>

                <motion.div
                    className="p-6 flex-1"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <h1 className="text-6xl md:text-6xl font-extrabold mb-4">
                        <span className={` ${animate ? 'animate-pulse-text' : ''}`}>Hi,</span> I'm Rami Ghaleb
                    </h1>
                    <AnimatePresence mode="wait">
                        <motion.p
                            key={currentStatement}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.8 }}
                            className={`text-xl md:text-2xl font-semibold`}
                        >
                            {statements[currentStatement]}
                        </motion.p>
                    </AnimatePresence>
                    <p className="text-lg md:text-xl mt-4">
                        I'm a passionate Software Engineer and student at Virginia Tech, specializing in machine learning, cybersecurity, and software development. Let's build something amazing together.
                    </p>

                    <div className="flex flex-col md:flex-row gap-4 mt-6">
                        <a
                            href="https://drive.google.com/file/d/12lIlOjr7K8E5phbehCNtq3-20lgoGqfh/view?usp=sharing"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center justify-center px-6 py-3 bg-[#10000] text-[#39ff14] font-bold rounded-lg shadow-lg border-4 border-[#39ff14] ${animate ? 'animate-pulse-border' : ''} hover:bg-red-600 transition duration-200`}
                        >
                            <FaFileDownload className="mr-2" />
                            Resume
                        </a>

                        <a
                            href="https://drive.google.com/file/d/1Q4EdQFNCD6CLwsT41cG56wtaY2QsPKG_/view?usp=sharing"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center justify-center px-6 py-3 bg-[#10000] text-[#39ff14] font-bold rounded-lg shadow-lg border-4 border-[#39ff14] ${animate ? 'animate-pulse-border' : ''} hover:bg-red-600 transition duration-200`}
                        >
                            <FaFileDownload className="mr-2" />
                            Transcript
                        </a>

                        <motion.button
                            className={`flex items-center justify-center px-6 py-3 bg-[#10000] text-[#39ff14] font-bold rounded-lg shadow-lg border-4 border-[#39ff14] ${animate ? 'animate-pulse-border' : ''} hover:bg-red-600 transition duration-200`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleNavigate('experience')}
                        >
                            <FaBriefcase className="mr-2" />
                            Experiences
                        </motion.button>

                        <motion.button
                            className={`flex items-center justify-center px-6 py-3 bg-[#10000] text-[#39ff14] font-bold rounded-lg shadow-lg border-4 border-[#39ff14] ${animate ? 'animate-pulse-border' : ''} hover:bg-red-600 transition duration-200`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleNavigate('contact')}
                        >
                            <FaPhone className="mr-2" />
                            Get in Touch
                        </motion.button>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Overview;
